<template>
    <div>
        <div class="builder-layout">
            <!-- Palette -->
            <MountedTeleport to="#sidebar">
                <div v-if="!hasNestedBlocks" id="builder-palette">
                    <builder-sidebar-segment v-show="types && types.length > 0">
                        <builder-sidebar-headline>
                            {{ lang('content::content.types.palette.general') }}
                        </builder-sidebar-headline>
                        <builder-sidebar-content>
                            <Sortable
                                v-if="types"
                                :list="types"
                                item-key="name"
                                tag="div"
                                :options="paletteOptions"
                                class="builder-palette__grid"
                                :class="[`builder-palette__grid-cols-${viewOptions?.palette?.cols ?? 2}`]"
                                @end="paletteEnd($event, 'types')"
                            >
                                <template #item="{ element: element }">
                                    <div
                                        class="builder-palette__item cursor-grab"
                                        :data-slug="element.slug"
                                        :data-type="element.type"
                                        :data-edit="element.edit"
                                    >
                                        <div class="builder-palette__item-inner">
                                            <div class="builder-palette__item-icon-wrapper">
                                                <load-file
                                                    replace-with
                                                    :src="element.image"
                                                    class="builder-palette__item-icon"
                                                ></load-file>
                                            </div>
                                            <div class="builder-palette__item-icon-text">
                                                {{ element.name }}
                                            </div>
                                        </div>
                                    </div>
                                </template>
                            </Sortable>
                        </builder-sidebar-content>
                    </builder-sidebar-segment>

                    <builder-sidebar-segment v-show="blocks && blocks.length > 0">
                        <builder-sidebar-headline>
                            {{ lang('content::content.types.palette.blocks') }}
                        </builder-sidebar-headline>
                        <builder-sidebar-content>
                            <Sortable
                                v-if="blocks"
                                :list="blocks"
                                item-key="id"
                                tag="div"
                                :options="paletteOptions"
                                class="builder-palette__grid"
                                :class="[`grid-cols-${viewOptions?.palette?.cols ?? 2}`]"
                                @end="paletteEnd($event, 'blocks')"
                            >
                                <template #item="{ element: element }">
                                    <div
                                        class="builder-palette__item cursor-grab"
                                        :data-slug="element.slug"
                                        :data-type="element.type"
                                        :data-edit="element.edit"
                                        :data-id="element.id"
                                    >
                                        {{ element.name }}
                                    </div>
                                </template>
                            </Sortable>
                        </builder-sidebar-content>
                    </builder-sidebar-segment>
                </div>
            </MountedTeleport>
            <!-- Content -->
            <div
                v-if="hasNestedBlocks"
                class="builder-v1-guard bg-amber-50 border border-amber-300 text-amber-800 text-sm rounded p-3 mb-3"
            >
                {{ lang('content::content.builder_v2.v1_nested_readonly') }}
            </div>
            <div
                id="builder-content"
                class="builder-content"
                :class="[canvasClass, { 'pointer-events-none opacity-60': hasNestedBlocks }]"
                :style="canvasStyle"
            >
                <Sortable
                    v-if="modelValue"
                    :list="modelValue"
                    item-key="id"
                    tag="section"
                    :options="builderOptions"
                    class="builder-content__sortable"
                    @end="builderEnd"
                >
                    <template #item="{ element: level, index: levelIndex }">
                        <!-- Level -->
                        <div
                            v-show="domain === null || !level.domain || domain === level.domain"
                            :id="`level-${levelIndex}`"
                            class="builder-level level"
                        >
                            <!-- Level inside -->
                            <div
                                class="builder-level__inside level__inside"
                                :class="[
                                    opened === levelIndex
                                        ? 'builder-level__inside--active'
                                        : 'builder-level__inside--inactive',
                                ]"
                            >
                                <!-- Level: Controls -->
                                <div
                                    class="builder-level__controls level__controls"
                                    :class="[
                                        opened === levelIndex
                                            ? 'builder-level__controls--visible'
                                            : 'builder-level__controls--hidden',
                                    ]"
                                >
                                    <button
                                        class="builder-level__control-btn builder-level__control-btn--drag cursor-grab"
                                        type="button"
                                    >
                                        <icon
                                            icon="hero/micro/chevron-up-down"
                                            class="builder-level__control-icon"
                                        ></icon>
                                    </button>
                                    <button
                                        class="builder-level__control-btn"
                                        :class="{ 'builder-level__control-btn--active': opened === levelIndex }"
                                        type="button"
                                        @click="toggleLevelSettings(levelIndex)"
                                    >
                                        <icon
                                            icon="hero/micro/adjustments-horizontal"
                                            class="builder-level__control-icon"
                                        ></icon>
                                    </button>
                                    <button
                                        :title="lang('general.datagrid.duplicate')"
                                        class="builder-level__control-btn"
                                        type="button"
                                        @click="duplicateContentRow(levelIndex)"
                                    >
                                        <icon icon="hero/micro/clipboard" class="builder-level__control-icon"></icon>
                                    </button>
                                    <button
                                        :title="lang('general.datagrid.delete')"
                                        class="builder-level__control-btn"
                                        type="button"
                                        @click="removeContentRow(levelIndex)"
                                    >
                                        <icon icon="hero/micro/x-mark" class="builder-level__control-icon"></icon>
                                    </button>
                                </div>

                                <!-- Level: Options -->
                                <main>
                                    <MountedTeleport v-if="opened === levelIndex" to="#aside">
                                        <builder-sidebar-segment>
                                            <builder-sidebar-headline>
                                                <template v-if="level.useBlock && level.useBlock !== '0'">
                                                    {{
                                                        lang('fields::fields.builder.settings.header', {
                                                            name: getBlockName(level.type),
                                                        })
                                                    }}
                                                </template>
                                                <template v-else>
                                                    {{
                                                        lang('fields::fields.builder.settings.header', {
                                                            name: getTypeName(level.type),
                                                        })
                                                    }}
                                                </template>
                                            </builder-sidebar-headline>
                                            <builder-sidebar-content>
                                                <template v-if="level.useBlock && level.useBlock !== '0'">
                                                    <div>
                                                        <Label
                                                            :value="lang('content::content.blocks.select_type')"
                                                            size="xs"
                                                        >
                                                        </Label>
                                                        <AdminSelect
                                                            v-model="level.type"
                                                            :options="blocks"
                                                            property="id"
                                                            size="xs"
                                                        />
                                                    </div>
                                                </template>

                                                <div class="builder-compact">
                                                    <div
                                                        v-for="attribute in filterAttributeSegment(
                                                            levelAttributes(level),
                                                            null,
                                                        )"
                                                        :key="attribute.slug"
                                                    >
                                                        <Field
                                                            v-if="level.value"
                                                            v-model="level.value[attribute.slug]"
                                                            :field="attribute"
                                                            :multiple="
                                                                attribute.multiple === true ||
                                                                attribute.multiple === '1'
                                                            "
                                                            :dusk="`form-content-${levelIndex}-${attribute.slug}`"
                                                            :searchable="false"
                                                            label-position="top"
                                                            size="xs"
                                                        />
                                                    </div>
                                                </div>

                                                <div
                                                    v-if="filterAttributeSegment(levelAttributes(level), 'css').length > 0"
                                                    class="pt-2 border-t border-neutral-100 dark:border-neutral-800 builder-compact"
                                                >
                                                    <h4 class="text-xs">{{ lang('fields::fields.segments.css') }}</h4>
                                                    <div
                                                        v-for="attribute in filterAttributeSegment(
                                                            levelAttributes(level),
                                                            'css',
                                                        )"
                                                        :key="attribute.slug"
                                                        class="level__options"
                                                    >
                                                        <Field
                                                            v-if="level.value"
                                                            v-model="level.value[attribute.slug]"
                                                            :field="attribute"
                                                            :dusk="`form-content-${levelIndex}-${attribute.slug}`"
                                                            label-position="left"
                                                            size="xs"
                                                        />
                                                    </div>
                                                </div>
                                            </builder-sidebar-content>
                                        </builder-sidebar-segment>

                                        <!-- Select domain, where to use this content -->
                                        <builder-sidebar-segment v-if="domains && domains.length > 0">
                                            <builder-sidebar-headline>
                                                {{
                                                    lang('content::content.types.domain', {
                                                        name: getTypeName(level.type),
                                                    })
                                                }}
                                            </builder-sidebar-headline>
                                            <builder-sidebar-content>
                                                <Label :value="lang('content::content.types.domain')" size="xs"></Label>
                                                <AdminSelect v-model="level.domain" :options="domains" size="xs" />
                                            </builder-sidebar-content>
                                        </builder-sidebar-segment>

                                        <!-- Link to block edit -->
                                        <builder-sidebar-segment v-if="level.useBlock && level.useBlock !== '0'">
                                            <builder-sidebar-headline>
                                                {{
                                                    lang('fields::fields.builder.block.header', {
                                                        name: getBlockName(level.type),
                                                    })
                                                }}
                                            </builder-sidebar-headline>
                                            <builder-sidebar-content>
                                                <p class="py-4 text-xs">
                                                    {{ lang('fields::fields.builder.block.description') }}
                                                </p>
                                                <div class="text-center">
                                                    <Button
                                                        :href="route(`content/blocks.admin.edit`, { id: level.type })"
                                                        :a="true"
                                                        size="sm"
                                                        target="_blank"
                                                        dusk="block-edit"
                                                    >
                                                        {{ lang(`fields::fields.builder.block.actions.edit`) }}
                                                    </Button>
                                                </div>
                                            </builder-sidebar-content>
                                        </builder-sidebar-segment>
                                    </MountedTeleport>

                                    <template v-if="level.useBlock && level.useBlock !== '0'">
                                        <!-- Level: Use prepared block -->
                                        <div class="level__options">
                                            <BlockPreview :type="level.type" />
                                        </div>
                                    </template>
                                    <template v-else>
                                        <!-- Level: edit view -->
                                        <div v-if="levelEdit(level)">
                                            <!-- Level: defined edit view -->
                                            <component
                                                :is="levelEdit(level)"
                                                v-model="level.value"
                                                :attributes="filterAttributeSegment(levelAttributes(level), null)"
                                                :level-index="levelIndex"
                                                :opened-index="opened"
                                            />
                                        </div>
                                        <div v-else>
                                            <!-- Level: generic edit view -->
                                            <BlockPreviewRaw :type="level.type" :value="level.value" />
                                            <component :is="`Level${level.type}`" v-model="level.value" />
                                        </div>
                                    </template>
                                </main>
                            </div>
                        </div>
                    </template>
                </Sortable>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { usePage } from '@inertiajs/vue3';
import { arrayMove } from '@src/Common/Helpers/array';
import type { Level } from '@src/Content/Pages/types';
import BlockPreview from '@src/Content/Pages/Blocks/Preview.vue';
import BlockPreviewRaw from '@src/Content/Pages/Blocks/PreviewRaw.vue';
import {
    contentHasNestedBlocks,
    filterAttributeSegment,
    resolveLevelAttributes,
    resolveLevelEdit,
} from '@src/Content/Pages/Builder/helpers';
import MountedTeleport from '@src/Common/Components/MountedTeleport.vue';
import BuilderSidebarHeadline from '@src/Content/Pages/Builder/Partials/SidebarHeadline.vue';
import BuilderSidebarSegment from '@src/Content/Pages/Builder/Partials/SidebarSegment.vue';
import BuilderSidebarContent from '@src/Content/Pages/Builder/Partials/SidebarContent.vue';
import type { FieldComponent } from '@src/Common/Components/types';
import { useBuilderTheme } from '@src/Content/Pages/Builder/useBuilderTheme';

const { canvasStyle, canvasClass } = useBuilderTheme();

const modelValue = defineModel<Array<any>>({
    default: () => [],
});

const props = withDefaults(defineProps<FieldComponent>(), {
    domains: () => [],
    domain: null,
    blocks: () => null,
    types: () => null,
    viewOptions: () => ({
        palette: {
            cols: 2,
        },
    }),
});

const $page = usePage();

const domain = ref(props.domain);
const domains = ref(props.domains ?? []);

const blocks = ref(props.blocks ? props.blocks : ($page.props.blocks ?? null));
const types = ref(props.types ? props.types : ($page.props.types ?? null));

const viewOptions = ref(props.viewOptions);

const loadBlocks = async () => {
    axios.get(route('content.builder.blocks')).then(({ data }) => {
        blocks.value = data.blocks;
    });
};

const loadTypes = async () => {
    axios.get(route('content.builder.types')).then(({ data }) => {
        // v1 palette must never offer v2 containers, whatever the endpoint returns.
        types.value = data.types.filter((t) => !t.container);
    });
};

if (!blocks.value) {
    loadBlocks();
}

if (!types.value) {
    loadTypes();
}

const getTypeInfo = (type: string) => {
    const found = types.value.find((t) => t.slug === type);
    if (found) {
        return found;
    }

    const foundBlock = blocks.value.find((b) => b.id == type);
    if (foundBlock) {
        return foundBlock;
    }

    return {
        name: type,
    };
};

// v2-published levels lose `edit`/`attributes` (NormalizeLevelTree strips
// editor artifacts) — hydrate them back from the types registry at render
const levelEdit = (level: Level) => resolveLevelEdit(level, types.value ? getTypeInfo(level.type) : null);

const levelAttributes = (level: Level) => resolveLevelAttributes(level, types.value ? getTypeInfo(level.type) : null);

const modifyLevelType = (level: Level) => {
    const type = getTypeInfo(level.type);

    if (!type || !type.attributes) {
        return;
    }

    const value = type.attributes.reduce((acc, attribute) => {
        acc[attribute.slug] = attribute.default ?? null;

        return acc;
    }, {});

    level.value = value;
    level.attributes = type.attributes;
};

const removeContentRow = (index: number) => {
    modelValue.value.splice(index, 1);
};

const duplicateContentRow = (index: number) => {
    const level = JSON.parse(JSON.stringify(modelValue.value[index]));

    level.id = Date.now();

    modelValue.value.splice(index, 0, level);
};

/**
 * When builder item is moved
 */
const builderEnd = (event) => {
    const content = arrayMove([...modelValue.value], event.oldIndex, event.newIndex);

    modelValue.value = content;
};

const paletteOptions = {
    group: {
        name: 'shared',
        pull: 'clone',
        put: false,
    },
    animation: 150,
    sort: false,
    ghostClass: 'palette-ghost',
    dragClass: 'palette-drag',
    chosenClass: 'palette-chosen',
};

// v2 nested content (children) would be silently destroyed by v1 editing —
// detecting it flips the whole v1 builder read-only (banner above canvas)
const hasNestedBlocks = computed(() => contentHasNestedBlocks(modelValue.value));

const builderOptions = {
    group: paletteOptions.group.name,
    ghostClass: 'builder-ghost',
    dragClass: 'builder-drag',
    chosenClass: 'builder-chosen',
};

/**
 * When palette item is dropped into the builder
 */
const paletteEnd = (event, paletteName = 'types') => {
    if (event.to === event.from) {
        return;
    }

    const source = event.clone;

    const id = Date.now();

    const level: Level = {
        id: id,
        custom: false,
        useBlock: paletteName === 'blocks',
        type: paletteName === 'types' ? source.dataset.slug : parseInt(source.dataset.id, 10),
        value: paletteName === 'types' ? source.dataset.slug : undefined,
        attributes: null,
        settings: {},
        domain: null,
        edit: source.dataset.edit,
    };

    modelValue.value.splice(event.newIndex, 0, level);

    modifyLevelType(level);

    event.item.remove();
};

const getBlockName = (id: string) => {
    const block = blocks.value.find((b) => b.id == id);

    return block ? block.name : id;
};

const getTypeName = (slug: string) => {
    const type = types.value.find((t) => t.slug === slug);

    return type ? type.name : slug;
};

/**
 * Level settings
 */
const opened = ref(null);

const toggleLevelSettings = (index: number) => {
    if (opened.value === index) {
        opened.value = null;
    } else {
        opened.value = index;
    }
};

watch(
    () => props.types,
    () => {
        if (props.types) {
            types.value = props.types;
        }
    },
    { immediate: true },
);

watch(
    () => props.blocks,
    () => {
        if (props.blocks) {
            blocks.value = props.blocks;
        }
    },
    { immediate: true },
);

watch(
    () => props.domain,
    () => {
        domain.value = props.domain;
    },
    { immediate: true },
);
</script>
